import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FileText, AlertCircle, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const inputStyle = {
  width: '100%', padding: '0.65rem 0.875rem',
  border: '1.5px solid #d1d5db', borderRadius: 8,
  fontSize: '0.95rem', color: '#111', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit',
  transition: 'border-color 0.15s', background: '#fff',
};

const btnStyle = (disabled) => ({
  width: '100%', padding: '0.75rem 1rem',
  background: disabled ? '#d1d5db' : '#111',
  color: '#fff', border: 'none', borderRadius: 8,
  fontSize: '0.95rem', fontWeight: 600,
  cursor: disabled ? 'default' : 'pointer',
  fontFamily: 'inherit', transition: 'background 0.15s',
});

const labelStyle = {
  display: 'block', fontSize: '0.82rem', fontWeight: 600,
  color: '#374151', marginBottom: '0.375rem',
};

const cardStyle = {
  background: '#fff', border: '1px solid #e5e7eb',
  borderRadius: 16, padding: '2.5rem 2rem',
  width: '100%', maxWidth: 420, boxSizing: 'border-box',
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
};

const containerStyle = {
  minHeight: '100vh', display: 'flex', justifyContent: 'center',
  alignItems: 'flex-start', background: '#f4f4f5',
  padding: '3rem 1.25rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

function Logo() {
  return (
    <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
      <div style={{
        display: 'inline-block', background: '#111', borderRadius: 10,
        padding: '8px 18px',
      }}>
        <span style={{ color: '#fff', fontWeight: 800, fontSize: '1rem', letterSpacing: '-0.5px' }}>DealNexa</span>
      </div>
    </div>
  );
}

// ─── State 1: Accept a first-time invite ─────────────────────────────────────
function AcceptInviteView({ token }) {
  const navigate = useNavigate();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/auth/invite-info?token=${token}`)
      .then(({ data }) => {
        setInviteInfo(data);
        setDisplayName(data.email?.split('@')[0] || '');
      })
      .catch(err => setError(err.response?.data?.error || 'This invite link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/portal/auth/accept-invite`, {
        token, displayName: displayName.trim(),
      });
      localStorage.setItem('portalToken', data.token);
      localStorage.setItem('portalSession', JSON.stringify(data.session));
      navigate('/portal');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept invite. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Loading invite…</div>
  );

  if (error) return (
    <div style={{ textAlign: 'center' }}>
      <AlertCircle size={36} color="#ef4444" style={{ marginBottom: '0.75rem' }} />
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111', margin: '0 0 0.5rem' }}>Invite Unavailable</h2>
      <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 1.5rem' }}>{error}</p>
      <p style={{ color: '#9ca3af', fontSize: '0.82rem' }}>
        Already used this link? <a href="/portal/login" style={{ color: '#111', fontWeight: 600 }}>Log in here</a>
      </p>
    </div>
  );

  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{
          width: 52, height: 52, borderRadius: 13, background: '#f0f4ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
        }}>
          <FileText size={24} color="#4F46E5" />
        </div>
        <span style={{
          display: 'inline-block', background: '#f0f4ff', color: '#4F46E5',
          fontSize: '0.78rem', fontWeight: 700, padding: '3px 12px', borderRadius: 20,
          marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>
          {inviteInfo.role} Portal
        </span>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111', margin: '0 0 0.25rem' }}>
          You're invited
        </h2>
        <p style={{ color: '#666', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>
          <strong style={{ color: '#111' }}>{inviteInfo.buyerOrgName}</strong> has invited you to share documents for
        </p>
        <p style={{ color: '#111', fontWeight: 700, fontSize: '1rem', margin: '4px 0 0' }}>
          {inviteInfo.dealName}
        </p>
      </div>

      <form onSubmit={handleAccept}>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Your name</label>
          <input
            type="text" placeholder="Enter your name"
            value={displayName} onChange={e => setDisplayName(e.target.value)}
            required autoFocus style={inputStyle}
            onFocus={e => e.target.style.borderColor = '#111'}
            onBlur={e => e.target.style.borderColor = '#d1d5db'}
          />
          <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '0.375rem 0 0' }}>
            This is how you'll appear in the portal.
          </p>
        </div>
        <button type="submit" disabled={submitting || !displayName.trim()} style={btnStyle(submitting || !displayName.trim())}>
          {submitting ? 'Opening portal…' : 'Open Deal Portal →'}
        </button>
      </form>

      <div style={{
        marginTop: '1.25rem', padding: '0.875rem 1rem',
        background: '#f9fafb', borderRadius: 8, fontSize: '0.82rem', color: '#555', border: '1px solid #f3f4f6',
      }}>
        <strong style={{ color: '#333' }}>What you can do in this portal:</strong>
        <ul style={{ margin: '0.375rem 0 0', paddingLeft: '1.25rem', lineHeight: 1.9, color: '#666' }}>
          <li>Upload and submit documents</li>
          <li>View and fulfil document request checklists</li>
          <li>Message the deal team directly</li>
        </ul>
      </div>
    </>
  );
}

// ─── State 2: Verify a relogin magic link ────────────────────────────────────
function VerifyReloginView({ reloginToken }) {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    axios.post(`${API_URL}/api/portal/auth/verify-link`, { token: reloginToken })
      .then(({ data }) => {
        localStorage.setItem('portalToken', data.token);
        localStorage.setItem('portalSession', JSON.stringify(data.session));
        navigate('/portal');
      })
      .catch(err => setError(err.response?.data?.error || 'This login link is invalid or has expired.'));
  }, [reloginToken, navigate]);

  if (error) return (
    <div style={{ textAlign: 'center' }}>
      <AlertCircle size={36} color="#ef4444" style={{ marginBottom: '0.75rem' }} />
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111', margin: '0 0 0.5rem' }}>Link Expired</h2>
      <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 1.5rem' }}>{error}</p>
      <a href="/portal/login" style={{
        display: 'inline-block', background: '#111', color: '#fff',
        padding: '0.625rem 1.25rem', borderRadius: 8, fontWeight: 600,
        fontSize: '0.875rem', textDecoration: 'none',
      }}>Request a new link</a>
    </div>
  );

  return (
    <div style={{ textAlign: 'center', padding: '1rem 0' }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        border: '3px solid #e5e7eb', borderTopColor: '#111',
        animation: 'spin 0.75s linear infinite', margin: '0 auto 1rem',
      }} />
      <p style={{ color: '#666', fontSize: '0.9rem' }}>Logging you in…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── State 3: No token — request a login link ─────────────────────────────────
function RequestLinkView() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/portal/auth/request-link`, { email: email.trim() });
    } catch {}
    finally {
      setSubmitting(false);
      setSent(true); // always show success — prevents email enumeration
    }
  };

  if (sent) return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 52, height: 52, borderRadius: '50%', background: '#ecfdf5',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
      }}>
        <CheckCircle size={26} color="#10b981" />
      </div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111', margin: '0 0 0.5rem' }}>Check your email</h2>
      <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 1.5rem', lineHeight: 1.6 }}>
        If <strong style={{ color: '#111' }}>{email}</strong> has portal access, we've sent a login link. It expires in 15 minutes.
      </p>
      <button
        onClick={() => { setSent(false); setEmail(''); }}
        style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '0.82rem', cursor: 'pointer' }}
      >
        Try a different email
      </button>
    </div>
  );

  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{
          width: 52, height: 52, borderRadius: 13, background: '#f0f4ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
        }}>
          <Mail size={24} color="#4F46E5" />
        </div>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111', margin: '0 0 0.375rem' }}>Log in to your portal</h2>
        <p style={{ color: '#666', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>
          Enter your email and we'll send you a login link.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Email address</label>
          <input
            type="email" required placeholder="you@example.com"
            value={email} onChange={e => setEmail(e.target.value)}
            autoFocus style={inputStyle}
            onFocus={e => e.target.style.borderColor = '#111'}
            onBlur={e => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <button type="submit" disabled={submitting || !email.trim()} style={btnStyle(submitting || !email.trim())}>
          {submitting ? 'Sending…' : 'Send login link →'}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.82rem', color: '#9ca3af' }}>
        First time here? You need an invite link from the deal team.
      </p>
    </>
  );
}

// ─── Root: route to the right view ───────────────────────────────────────────
export default function PortalAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const inviteToken  = searchParams.get('token');
  const reloginToken = searchParams.get('relogin');

  // If already have a valid session in localStorage, go straight to portal
  useEffect(() => {
    if (!inviteToken && !reloginToken) {
      const stored = localStorage.getItem('portalToken');
      if (stored) navigate('/portal');
    }
  }, [inviteToken, reloginToken, navigate]);

  let view;
  if (inviteToken)  view = <AcceptInviteView token={inviteToken} />;
  else if (reloginToken) view = <VerifyReloginView reloginToken={reloginToken} />;
  else              view = <RequestLinkView />;

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <Logo />
        {view}
      </div>
    </div>
  );
}
